'use client'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import replacePlaceholders from 'labels/lib/replacePlaceholders'
import { type RefObject, useRef } from 'react'
import {
	MINUTES_TO_TIMEOUT,
	USER_INACTIVITY_AGENT_TIMEOUT,
	USER_INACTIVITY_AGENT_WARNING,
	USER_INACTIVITY_CHAT_TIMEOUT,
	USER_INACTIVITY_CHAT_WARNING,
} from '../constants/timeout'
import { ConversationTrigger } from '../types/AIResponseTypes'
import type { GenerateAIResponseProps } from '../types/ChatMessagesContextTypes'
import type { ChatbotMessage } from '../types/messageTypes'
import { createAITextMessage } from '../utils/createAITextMessage'

const INTERACTION_INTERVAL_TIME = 1000

export const useChatbotTimeout = ({
	generateAIResponse,
	addMessagesHistory,
	isAgentConversationActiveRef,
	clearPullingInterval,
}: {
	generateAIResponse: (props: GenerateAIResponseProps) => Promise<void>
	addMessagesHistory: (messages: ChatbotMessage[]) => void
	isAgentConversationActiveRef: RefObject<boolean>
	clearPullingInterval: () => void
}) => {
	const { t } = useLabels()
	const userInteractionIntervalIDRef =
		useRef<ReturnType<typeof setTimeout>>(undefined)
	const agentTimeOutIDRef = useRef<ReturnType<typeof setTimeout>>(undefined)

	const generateTimeoutConversation = async (props?: {
		isTimeoutAgentMessage: boolean
	}) => {
		await generateAIResponse({
			conversationTrigger: ConversationTrigger.TimeoutConversation,
		})
		addMessagesHistory([
			createAITextMessage({
				textMessage: t('assistant.timeout.close.text'),
				isTimeoutAgentMessage: props?.isTimeoutAgentMessage,
			}),
		])
	}
	const clearAgentInterval = () => {
		clearInterval(agentTimeOutIDRef.current)
	}
	const clearChatInteractionInterval = () => {
		clearInterval(userInteractionIntervalIDRef.current)
	}
	const initChatInteractionInterval = () => {
		clearInterval(userInteractionIntervalIDRef.current)
		let timeLapsedWithoutInteraction = 0
		userInteractionIntervalIDRef.current = setInterval(async () => {
			timeLapsedWithoutInteraction += 1000
			if (timeLapsedWithoutInteraction === USER_INACTIVITY_CHAT_WARNING) {
				addMessagesHistory([
					createAITextMessage({
						textMessage: replacePlaceholders(
							t('assistant.timeout.notice.text'),
							{
								1: MINUTES_TO_TIMEOUT,
							}
						),
					}),
				])
			}
			if (timeLapsedWithoutInteraction === USER_INACTIVITY_CHAT_TIMEOUT) {
				await generateTimeoutConversation()
				clearInterval(userInteractionIntervalIDRef.current)
			}
		}, INTERACTION_INTERVAL_TIME)
	}
	const initAgentResponseTimeout = () => {
		clearInterval(agentTimeOutIDRef.current)
		let timeLapsedWithoutInteraction = 0
		agentTimeOutIDRef.current = setInterval(async () => {
			timeLapsedWithoutInteraction += 1000
			if (timeLapsedWithoutInteraction === USER_INACTIVITY_AGENT_WARNING) {
				addMessagesHistory([
					createAITextMessage({
						textMessage: replacePlaceholders(
							t('assistant.timeout.notice.text'),
							{
								1: MINUTES_TO_TIMEOUT,
							}
						),
					}),
				])
			}
			if (timeLapsedWithoutInteraction === USER_INACTIVITY_AGENT_TIMEOUT) {
				await generateTimeoutConversation({ isTimeoutAgentMessage: true })
				clearPullingInterval()
				clearInterval(userInteractionIntervalIDRef.current)
				isAgentConversationActiveRef.current = false
			}
		}, INTERACTION_INTERVAL_TIME)
	}
	return {
		initChatInteractionInterval,
		initAgentResponseTimeout,
		clearAgentInterval,
		clearChatInteractionInterval,
	}
}
